// src/pages/Home/Stats/Stats.js

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styles from './Stats.module.css';

const StatCard = ({
  icon,
  start,
  targetNumber,
  suffix,
  label,
  backgroundColor,
  inView
}) => {
  const [count, setCount] = useState(start);
  const frameRef = useRef();

  useEffect(() => {
    if (!inView) return;

    const duration = 2000;               // total animation time in ms
    const range = targetNumber - start;  // how much to count up
    const startTime = performance.now();

    const step = (timestamp) => {
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setCount(Math.floor(start + range * progress));

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step);
      }
    };

    frameRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frameRef.current);
  }, [inView, start, targetNumber]);

  return (
    <div className={styles.card}>
      <div
        className={styles.iconWrapper}
        style={{ backgroundColor }}
      >
        <div className={styles.icon}>{icon}</div>
      </div>
      <div className={styles.number}>
        {count.toLocaleString()}<span className={styles.suffix}>{suffix}</span>
      </div>
      <div className={styles.label}>{label}</div>
    </div>
  );
};

StatCard.propTypes = {
  icon: PropTypes.node.isRequired,
  start: PropTypes.number.isRequired,
  targetNumber: PropTypes.number.isRequired,
  suffix: PropTypes.string,
  label: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string,
  inView: PropTypes.bool.isRequired,
};
StatCard.defaultProps = {
  suffix: '',
  backgroundColor: '#007cfb',
};

const Stats = ({ data }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div className={styles.container} ref={containerRef}>
      {data.map((stat) => (
        <StatCard
          key={stat.id}
          icon={stat.icon}
          start={stat.start}
          targetNumber={stat.targetNumber}
          suffix={stat.suffix}
          label={t(`home.stats.${stat.label}`)}
          backgroundColor={stat.backgroundColor}
          inView={inView}
        />
      ))}
    </div>
  );
};

Stats.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      icon: PropTypes.node.isRequired,
      start: PropTypes.number,
      targetNumber: PropTypes.number.isRequired,
      suffix: PropTypes.string,
      label: PropTypes.string.isRequired,
      backgroundColor: PropTypes.string,
    })
  ).isRequired,
};

export default React.memo(Stats);
